import React, { useState, useRef, useEffect } from "react";
import { Link, NavLink } from "react-router-dom";
import { LuMenu } from "react-icons/lu";
import { FaRegUser } from "react-icons/fa";
import { AiOutlineShoppingCart } from "react-icons/ai";
import { BsPostcardHeart } from "react-icons/bs";
import { useAuth } from "../../context/AuthContext";
import { MdArrowDropDown } from "react-icons/md";
import { BASE_URL, USER_URL } from "../../constant/api";
import axios from "axios";
import SearchNav from "../Home/SearchNav/SearchNav";

function Navbar() {
  const { user, isAuthenticated, logout } = useAuth();
  const [isOpen, setIsOpen] = useState(false); // Mobile menu toggle
  const navbarRef = useRef(null);
  const mobileDropdownRef = useRef(null); // Ref for mobile dropdown
  const [isDropdownOpen, setIsDropdownOpen] = useState(false); // Desktop user dropdown
  const [isMobileDropdownOpen, setIsMobileDropdownOpen] = useState(false); // Mobile user dropdown
  const [isLoading, setIsLoading] = useState(true); // Loading state for fetching user data
  const [personalInfo, setPersonalInfo] = useState({});
  const [countries, setCountries] = useState([]); // Initialize as array
  const [selected, setSelected] = useState(null); // Selected country (object from countries list)
  const [isOpenCountry, setIsOpenCountry] = useState(false); // Country dropdown toggle
  const dropdownRef = useRef(null);

  const toggleMenu = () => setIsOpen((prev) => !prev);
  const toggleDropdown = () => setIsDropdownOpen((prev) => !prev);
  const toggleMobileDropdown = () => setIsMobileDropdownOpen((prev) => !prev);

  // Close the navbar if clicked outside, except for the dropdown menus
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        navbarRef.current &&
        !navbarRef.current.contains(event.target) &&
        (!mobileDropdownRef.current ||
          !mobileDropdownRef.current.contains(event.target))
      ) {
        setIsOpen(false);
        setIsMobileDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () =>
      document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Fetch user profile info
  useEffect(() => {
    const fetchPersonalInfo = async () => {
      try {
        const response = await USER_URL.get(`/accounts/profile-update/`);
        // Assuming response.data.Data[0] includes a "country" property (a name string)
        setPersonalInfo(response.data.Data[0]);
        console.log(response);
      } catch (error) {
        console.error("Error fetching personal info:", error);
      } finally {
        setIsLoading(false);
      }
    };

    if (isAuthenticated) {
      fetchPersonalInfo();
    } else {
      setIsLoading(false);
    }
  }, [isAuthenticated]);

  // Fetch countries list
  useEffect(() => {
    const fetchCountry = async () => {
      try {
        const response = await axios.get(`${BASE_URL}/accounts/countries/`);
        setCountries(response.data.Data);
        console.log(response);
      } catch (error) {
        console.error("Error fetching countries:", error);
      } finally {
        setIsLoading(false);
      }
    };

    // Fetch countries regardless of authentication if needed
    fetchCountry();
  }, []);

  // Set default selected country based on personalInfo.country (if available) or first country in list
  useEffect(() => {
    if (countries.length > 0) {
      if (personalInfo && personalInfo.country) {
        // Compare case-insensitively to find the matching country by name
        const matchedCountry = countries.find(
          (country) =>
            country.name.toLowerCase() === personalInfo.country.toLowerCase()
        );
        if (matchedCountry) {
          setSelected(matchedCountry);
        } else {
          setSelected(countries[0]);
        }
      } else {
        setSelected(countries[0]);
      }
    }
  }, [countries, personalInfo]);

  // Close the country dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target)
      ) {
        setIsOpenCountry(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () =>
      document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSelect = (country) => {
    setSelected(country);
    setIsOpenCountry(false);
  };

  // Close the menu on resize for larger screens
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 768) {
        setIsOpen(false);
        setIsMobileDropdownOpen(false);
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const renderNavigationLink = (to, label) => (
    <NavLink
      to={to}
      className={({ isActive }) =>
        isActive
          ? "text-brand-primary"
          : "text-brand-dark hover:text-brand-primary"
      }
    >
      {label}
    </NavLink>
  );

  // Skeleton component for the loading state
  const UserSkeleton = () => (
    <div className="flex  animate-pulse items-center gap-2">
      <div className="h-8 w-8 rounded-full bg-gray-300" />
      <div>
        <div className="h-3 w-16 rounded bg-gray-300" />
        <div className="mt-1 h-2 w-24 rounded bg-gray-300" />
      </div>
    </div>
  );

  return (
    <nav className="bg-white shadow-md p-2 text-base font-medium md:p-5">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8" ref={navbarRef}>
        <div className="flex h-16 items-center justify-between">
          {/* Logo and Country Dropdown */}
          <div className="flex items-center space-x-4">
            <Link to="/">
              <img
                src="/images/logo/icon_sg.png"
                // src="/images/logo/navlogo.png"
                alt="logo"
                className="h-10 w-10 rounded-full border-4 border-brand-primary"
              />
            </Link>
            <div className="relative inline-block text-left" ref={dropdownRef}>
            
              {isAuthenticated ? (
                <button
                  type="button"
                  onClick={() => setIsOpenCountry((prev) => !prev)}
                  className="inline-flex justify-between items-center rounded-md px-4 py-2 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none"
                >
                  <div className="flex items-center">
                    {selected && selected.flag ? (
                      <img
                        src={`${BASE_URL}${selected.flag}`}
                        alt={`${selected.name} flag`}
                        className="w-7 h-7 mr-2"
                      />
                    ) : null}
                    {/* Display a welcome message with the user's first name */}
                    <span>Welcome {personalInfo.first_name}</span>
                  </div>
                </button>
              ) : null}
              {isOpenCountry && (
                <div className="absolute mt-2 w-56 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-10">
                  <div className="py-1">
                    {countries.map((country) => (
                      <button
                        key={country.id}
                        onClick={() => handleSelect(country)}
                        className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center"
                      >
                        <img
                          src={`${BASE_URL}${country.flag}`}
                          alt={`${country.name} flag`}
                          className="w-7 h-7 mr-2"
                        />
                        <span>{country.name}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
          
          {/* Desktop Navigation */}
          <div className="hidden space-x-8 text-base uppercase lg:flex">
            {renderNavigationLink("/", "Home")}
            {/* {renderNavigationLink("/about", "About Us")} */}
            {/* {renderNavigationLink("/projects", "Projects")} */}
            {/* {renderNavigationLink("/services", "Services")} */}
            {renderNavigationLink("/contact-us", "Contact Us")}
            {user.user_type === "seller"
              ? renderNavigationLink("/seller", "Seller")
              : renderNavigationLink("/seller/info", "Seller")}
            {user.user_type === "customer" &&
              renderNavigationLink("/myorders", "My Orders")}
          </div>

          {/* Desktop Icons */}
          <div className="hidden items-center space-x-4 lg:flex">
            {user.user_type === "customer" && (
              <div className="flex items-center space-x-4">
                <NavLink to="/cart" className="flex items-center">
                  <AiOutlineShoppingCart className="text-3xl text-gray-700 hover:text-brand-primary" />
                </NavLink>
                <NavLink to="/wishlist" className="flex items-center">
                  <BsPostcardHeart className="text-3xl text-gray-700 hover:text-brand-primary" />
                </NavLink>
              </div>
            )}

            <SearchNav />

            <div className="flex items-center gap-5">
              {isAuthenticated ? (
                <div className="relative">
                  <div
                    className="flex cursor-pointer items-center"
                    onClick={toggleDropdown}
                  >
                    {isLoading ? (
                      <UserSkeleton />
                    ) : (
                      <div className="rounded-full border-2 border-gray-700 p-2">
                        <FaRegUser className="text-gray-700" />
                      </div>
                    )}
                  </div>
                  {/* {!personalInfo.is_profile_completed ? (
                    <div className="absolute right-0 my-2 rounded-xl w-48 p-[2px] border-2 border-red-500">
                      <div className="flex items-center text-[9px] sm:text-xs justify-center rounded-md">
                        <Link
                          to="/profile/edit_profile"
                          className="text-red-600 cursor-pointer"
                        >
                          Complete your profile!
                        </Link>
                      </div>
                    </div>
                  ) : null} */}
                  {/* Dropdown menu */}
                  {isDropdownOpen && (
                    <div className="absolute right-0 z-40 mt-2 w-48 rounded-lg border bg-white shadow-lg">
                      <Link
                        to="/profile/view"
                        className="block px-4 py-2 text-gray-700 hover:bg-gray-100"
                      >
                        Account
                      </Link>
                      {user.user_type === "customer" && (
                        <Link
                          to="/myorders"
                          className="block px-4 py-2 text-gray-700 hover:bg-gray-100"
                        >
                          My Orders
                        </Link>
                      )}
                      <Link
                        to="/profile/notification"
                        className="block px-4 py-2 text-gray-700 hover:bg-gray-100"
                      >
                        Notifications
                      </Link>
                      <Link
                        to="/profile/settings"
                        className="block px-4 py-2 text-gray-700 hover:bg-gray-100"
                      >
                        Settings
                      </Link>
                      <button
                        className="block w-full px-4 py-2 text-left text-gray-700 hover:bg-gray-100"
                        onClick={logout}
                      >
                        Logout
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <NavLink to="/login/email">
                  <button className="rounded-md border-2 border-gray-700 px-2 py-1 text-brand-dark hover:text-brand-primary">
                    Sign In
                  </button>
                </NavLink>
              )}
            </div>
          </div>

          {/* Mobile Menu Toggle */}
          <div className="flex items-center lg:hidden">
            <button
              onClick={toggleMenu}
              aria-expanded={isOpen}
              className="text-gray-700 focus:outline-none"
            >
              <LuMenu className="text-2xl" />
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      <div
        className={`${isOpen ? "max-h-screen opacity-100" : "max-h-0 opacity-0"
          } overflow-hidden transition-all duration-700 ease-in-out`}
      >
        <div className="px-2 pb-3 pt-2 sm:px-3">
          <div className="flex flex-col space-y-1 text-center uppercase">
            {renderNavigationLink("/", "Home")}
            {/* {renderNavigationLink("/about", "About Us")}
            {renderNavigationLink("/projects", "Projects")}
            {renderNavigationLink("/services", "Services")} */}
            {renderNavigationLink("/contact-us", "Contact Us")}
            {user.user_type === "seller"
              ? renderNavigationLink("/seller", "Seller")
              : renderNavigationLink("/seller/info", "Seller")}
            {user.user_type === "customer" &&
              renderNavigationLink("/myorders", "My Orders")}
          </div>

          {/* Mobile Icons */}
          <div className="flex flex-col items-center space-y-2 pt-2">
            {user.user_type === "customer" && (
              <div className="flex items-center space-x-1">
                <AiOutlineShoppingCart className="text-2xl text-gray-700" />
                {renderNavigationLink("/cart", "Cart")}
              </div>
            )}
            {user.user_type === "customer" && (
              <NavLink to="/wishlist">
                <BsPostcardHeart className="text-3xl text-gray-700 hover:text-brand-primary" />
              </NavLink>
            )}
            <div
              className="flex flex-col items-center space-y-2"
              ref={mobileDropdownRef}

            >
              {isAuthenticated ? (
                <div className="relative">
                  <div
                    className="flex cursor-pointer items-center"
                    onClick={toggleMobileDropdown}
                  >
                    {isLoading ? (
                      <UserSkeleton />
                    ) : (
                      <>
                        <div className="rounded-full border-2 border-gray-700 p-2">
                          <FaRegUser className="text-gray-700" />
                        </div>
                        <div className="ml-2">
                          <p className="text-sm font-semibold">
                            {personalInfo.username}
                          </p>
                          <p className="text-xs text-gray-500">
                            {personalInfo.email}
                          </p>
                        </div>
                        <MdArrowDropDown className="ml-1 text-xl text-gray-600" />
                      </>
                    )}
                  </div>
                  {/* {!personalInfo.is_profile_completed ? (
                    <div className="my-2 rounded-xl w-48 p-[2px] border-2 border-red-500">
                      <div className="flex items-center text-[9px] sm:text-xs justify-center rounded-md">
                        <Link
                          to="/profile/edit_profile"
                          className="text-red-600 cursor-pointer"
                        >
                          Complete your profile!
                        </Link>
                      </div>
                    </div>
                  ) : null} */}
                  {/* Mobile Dropdown menu */}
                  {isMobileDropdownOpen && (
                    <div className="relative z-10 mt-2 w-48 rounded-lg border bg-white shadow-lg">
                      <Link
                        to="/profile/view"
                        className="block px-4 py-2 text-gray-700 hover:bg-gray-100"
                      >
                        Account
                      </Link>
                      <Link
                        to="/myorders"
                        className="block px-4 py-2 text-gray-700 hover:bg-gray-100"
                      >
                        My Orders
                      </Link>
                      <Link
                        to="/profile/notification"
                        className="block px-4 py-2 text-gray-700 hover:bg-gray-100"
                      >
                        Notifications
                      </Link>
                      <Link
                        to="/profile/settings"
                        className="block px-4 py-2 text-gray-700 hover:bg-gray-100"
                      >
                        Settings
                      </Link>
                      <button
                        className="block w-full px-4 py-2 text-left text-gray-700 hover:bg-gray-100"
                        onClick={logout}
                      >
                        Logout
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <NavLink to="/login/email">
                  <button className="rounded-md border-2 border-gray-700 px-2 py-1 text-brand-dark hover:text-brand-primary">
                    Sign In
                  </button>
                </NavLink>
              )}
            </div>
          </div>
        </div>
      </div>
    </nav>
  );
}

export default Navbar;
